import React, { ChangeEvent, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Checkbox, Input } from "@citi-icg-172888/icgds-react";
import { useData } from "../../services/context/DataContext";
import SelectHoc from "../../common/SelectHoc";
import FooterButtonHoc from "../../common/FooterButtonHoc";
import { INIT_STEPS } from "../../services/constants/InitialConstants";
import PhoneComponent from "../../common/PhoneComponent";
import {
  checkErrorForPhoneAndEmail,
  getNextSteps,
  validateInputField,
} from "../../services/utils/utils";
import { CONTACT_INFO_INIT, IContactData } from "../../services/types/LeadRequisitionType";
import { INVALID_EMAIL_DOMAIN, EMAIL_PATTERN, VALID_EMAIL } from "../../constants/textConstants";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import CustomAlert from "../components/CustomAlert";
import { handleScript } from "../../services/utils/utils";

const ContactInfoPage: React.FC = () => {
  const {
    steps,
    setSteps,
    contactInfo,
    setContactInfo,
    setIsEditMode,
    staticData,
    setIsAboutBusiness,
    setCompanyInfo,
    setIsBasicInfo,
    setError,
    error,
    setIsCancelClicked,
  } = useData();
  const currentStep = 2;
  const divRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);
  const [invalidEmail, setInvalidEmail] = useState(error);
  const [invalidPhone, setInvalidPhone] = useState("");
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Partial<Record<string, boolean>>>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const customRef = useRef<HTMLDivElement>(null);
  const divMainRef = useRef<HTMLDivElement>(null);
  
  const validateFields = (
    updatedcontactInfo: IContactData | null,
    updatedTouchedFields = touchedFields
  ) => {
    const newErrors: Partial<Record<string, string>> = { ...errors };

    if (updatedTouchedFields.role && !validateInputField(updatedcontactInfo?.role)) {
      newErrors.role = "Please select role";
    } else delete newErrors.role;

    if (updatedTouchedFields.firstName && !validateInputField(updatedcontactInfo?.firstName)) {
      newErrors.firstName = "Please enter first name";
    } else delete newErrors.firstName;

    if (updatedTouchedFields.lastName && !validateInputField(updatedcontactInfo?.lastName)) {
      newErrors.lastName = "Please enter last name";
    } else delete newErrors.lastName;

    if (updatedTouchedFields.contactInfoConsent && !updatedcontactInfo?.contactInfoConsent) {
      newErrors.contactInfoConsent = "Please accept the contact Information Consent.";
    } else delete newErrors.contactInfoConsent;

    if (updatedTouchedFields.email && !validateEmail(updatedcontactInfo?.email ?? "")) {
      newErrors.email = "Please enter a valid email address.";
    } else delete newErrors.email;

    setErrors(newErrors);
    return _.isEmpty(newErrors);
  };

  const onChangeContactInfoHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedContactInfo: any = {
      ...contactInfo,
      [name]: value,
    };
    setContactInfo(updatedContactInfo);
    setTouchedFields((prev) => {
      const newTouchedFields = { ...prev, [name]: true };
      validateFields(updatedContactInfo, newTouchedFields);
      return newTouchedFields;
    });
  };

  const history = useNavigate();
  useEffect(() => {
    divRef.current?.focus();
    if (window.innerWidth < 768 && window.parent.location.href.includes("contact-us")) {
      window.parent.scroll(0, 0);
    } else if (window.parent.location.href.includes("contact-us")) {
      window.parent.scroll({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (contactInfo?.contactInfoConsent) {
      setContactInfo((prev) => ({ ...prev, contactInfoConsent: false }));
    }
  }, []);

  const onChangeContactEmailHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (validateEmail(value)) {
      if (
        staticData.personalEmailDomains.includes(
          value.split("@")[1].split(".")[0].toLocaleLowerCase()
        )
      ) {
        setInvalidEmail(INVALID_EMAIL_DOMAIN);
        setError(INVALID_EMAIL_DOMAIN);
      } else {
        setInvalidEmail(VALID_EMAIL);
        setError(VALID_EMAIL);
      }
    } else {
      setInvalidEmail("");
      setError("");
    }

    const updatedContactInfo: any = {
      ...contactInfo,
      [name]: value,
      contactInfoConsent: false,
    };
    setContactInfo(updatedContactInfo);
    setTouchedFields((prev) => {
      const newTouchedFields = { ...prev, [name]: true };
      validateFields(updatedContactInfo, newTouchedFields);
      return newTouchedFields;
    });
  };

  const onChangeCheckboxHandler = (event: any) => {
    if (event.type === "keydown" && event.key == "Enter") {
      event.preventDefault();
      event.target.checked = !event.target.checked;
    }
    const { name, checked } = event.target;
    const updatedContactInfo: any = {
      ...contactInfo,
      [name]: checked,
    };
    setContactInfo(updatedContactInfo);
    setTouchedFields((prev) => {
      const newTouchedFields = { ...prev, [name]: checked };
      validateFields(updatedContactInfo, newTouchedFields);
      return newTouchedFields;
    });
  };

  const updateCountry = (data: any) => {
    setContactInfo((prevData) => ({
      ...prevData,
      ["countryCode"]: data,
      contactInfoConsent: false,
    }));
    if (validateInputField(contactInfo?.mobile, "mobile") || !validateInputField(data)) {
      setInvalidPhone("");
    }
  };

  const updatePhone = (data: any) => {
    setContactInfo((prevData) => ({
      ...prevData,
      ["mobile"]: data,
      contactInfoConsent: false,
    }));
    if (validateInputField(data, "mobile")) {
      setInvalidPhone("");
    }
  };

  const validateEmail = (email: string) => {
    return EMAIL_PATTERN.test(email);
  };

  const onChangeDdlHandler = useCallback(
    (selectedParam: string) => {
      const updatedContactInfo: any = {
        ...contactInfo,
        ["role"]: selectedParam,
        contactInfoConsent: false,
      };
      setContactInfo(updatedContactInfo);
      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, ["role"]: true };
        validateFields(updatedContactInfo, newTouchedFields);
        return newTouchedFields;
      });
    },
    [setContactInfo, contactInfo]
  );

  const onSubmitHandler = () => {
    setIsSubmitClicked(true);
    if (enabledSubmit) {
      const updatedSteps = getNextSteps(steps, currentStep);
      setSteps(updatedSteps);
      setInvalidPhone("");
      handleScript(divMainRef, "ccbContactUsFormStep2ContactInfoCompleted");
    } else {
      if (
        !validateInputField(contactInfo?.mobile) ||
        !validateInputField(contactInfo?.countryCode)
      ) {
        setInvalidPhone("Please enter country code and number");
      }
      setTouchedFields(() => {
        const newTouchedFields = {
          role: true,
          firstName: true,
          lastName: true,
          email: true,
          contactInfoConsent: true,
        };
        validateFields(contactInfo, newTouchedFields);
        return newTouchedFields;
      });
      if (window.parent.location.href.includes("contact-us"))
        window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onCancelHandler = () => {
    setSteps(INIT_STEPS);
    setIsAboutBusiness(false);
    setIsEditMode(false);
    setContactInfo(CONTACT_INFO_INIT);
    setCompanyInfo(null);
    setIsBasicInfo(false);
    setError(null);
    
    history("/");
    setIsCancelClicked(true);
  };

  const onBackHandler = async () => {
    const newSteps = getNextSteps(steps, 0, false);
    setSteps(newSteps);
    setIsEditMode(false);
    setIsBasicInfo(true);
    if (window.parent.location.href.includes("contact-us"))
      window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const enabledSubmit = useMemo(() => {
    return (
      contactInfo?.role &&
      contactInfo?.firstName &&
      contactInfo?.lastName &&
      validateEmail(contactInfo?.email) &&
      contactInfo?.mobile &&
      contactInfo?.countryCode &&
      contactInfo?.contactInfoConsent
    );
  }, [contactInfo]);

  if (!contactInfo) {
    return <></>;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const pattern = /^[A-Za-z\s]*$/;

    if (pattern.test(value)) {
      onChangeContactInfoHandler(event);
      setContactInfo((prev) => ({ ...prev, contactInfoConsent: false }));
    }
  };

  const handleAlert = () => {
    setIsSubmitClicked(!isSubmitClicked);
  };

  return (
    <div>
      <div ref={divMainRef}></div>
      <div ref={divRef} className="lmn-dnca-w-l-100 lmn-dnca-w-m-100 lmn-dnca-w-s-100">
        {isSubmitClicked && Object.values(errors).length === 0 && (
          <CustomAlert
            ref={customRef}
            id="contactInfoPage-phone-email-alert"
            errors={checkErrorForPhoneAndEmail(invalidPhone, invalidEmail)}
            handleChange={handleAlert}
          />
        )}
      </div>
      <div className="lmn-dnca-w-l-100 lmn-dnca-w-m-100 lmn-dnca-w-s-100">
        {isSubmitClicked && Object.values(errors).length > 0 && (
          <CustomAlert
            ref={customRef}
            id="contactInfoPage-alert"
            errors={Object.values(errors)}
            handleChange={handleAlert}
          />
        )}
      </div>
      <div className="lmn-dnca-w-xl-60 lmn-dnca-w-l-60 lmn-dnca-w-m-60">
        <div className="lmn-mb-6">
          <div className="lmn-form-group lmn-mb-6">
            <label className="lmn-required" htmlFor="Your role">
              Your role
            </label>
            <SelectHoc
              // ref={divRef}
              id="your_role"
              label="Your role"
              options={staticData.roles}
              error={errors.role}
              value={contactInfo?.role}
              onChange={(options: any) => onChangeDdlHandler(options)}
            />
            {errors.role && <small >{errors.role}</small>}
          </div>
        </div>
        <div className="lmn-mb-6">
          <div className="lmn-form-group lmn-mb-5">
            <label className="lmn-required" htmlFor="your_first_name">
              First Name
            </label>
            <Input
              ref={firstNameRef}
              id="your_first_name"
              pattern="^[A-Za-z\s]+$"
              aria-label="First name"
              aria-required={true}
              aria-describedby="firstName" 
              name="firstName"
              value={contactInfo?.firstName}
              onChange={handleChange}
              invalid={!!errors.firstName}
            />
            {errors.firstName && <div aria-live="assertive" id="firstName" role="alert"> <small>{errors.firstName}</small> </div>}
          </div>
          <div className="lmn-form-group lmn-mb-5">
            <label className="lmn-required" htmlFor="your_last_name">
              Last Name
            </label>
            <Input
              ref={lastNameRef}
              id="your_last_name"
              pattern="^[A-Za-z\s]+$"
              aria-label="Last name"
              name="lastName"
              value={contactInfo?.lastName}
              onChange={handleChange}
              aria-describedby="lastName"
              aria-required={true}
              invalid={!!errors.lastName}
            />
            {errors.lastName && <div aria-live="assertive" id="lastName" role="alert"> <small>{errors.lastName}</small> </div>}
          </div>
        </div>
        <div className="lmn-form-group lmn-mb-6">
          <div className="lmn-mb-5">
            <label className="lmn-required" htmlFor="your_email">
              Email Address
            </label>
            <Input
              ref={emailRef}
              id="your_email"
              name="email"
              type="email"
              aria-label="Email"
              invalid={!!errors.email}
              aria-required={true}
              value={contactInfo?.email}
              onChange={(e) => onChangeContactEmailHandler(e)}
            />
            <div
              style={{
                color: invalidEmail ? "#FAB728" : "#ff7a75",
                fontSize: "12px",
                fontWeight: "400",
                marginTop: "0.25rem",
              }}
              aria-live="assertive" role="alert"
            >
              {!invalidEmail ? errors.email && errors.email : invalidEmail}
            </div>
          </div>
          <div className="lmn-mb-5">
            <label className="lmn-required" htmlFor="phone_number">
            Country Code & Phone number
            </label>
            <PhoneComponent
              id="input_phone_number"
              aria-describedby="phoneNumber"
              mobileNum={contactInfo?.mobile}
              countryCd={contactInfo?.countryCode}
              updatePhone={updatePhone}
              updateCountry={updateCountry}
              error={invalidPhone}
            />
            {invalidPhone && <div aria-live="assertive" id="phoneNumber" role="alert"> <small>{invalidPhone}</small> </div>}
          </div>
        </div>
        <div className="lmn-mb-6">
          <div className="lmn-d-flex">
            <Checkbox
              ref={consentRef}
              id="consent_cb"
              aria-label="I acknowledge Citi may collect, use, share, store, transfer and process my information
              as described in the Citi Commercial Bank Website Privacy Policy for the purposes of
              having a CCB Relationship Manager contact me."
              name="contactInfoConsent"
              checked={contactInfo?.contactInfoConsent ?? false}
              aria-required={true}
              aria-describedby="consent_name"
              onChange={onChangeCheckboxHandler}
              onKeyDown={(event: any) => onChangeCheckboxHandler(event)}
              tabIndex={0}
            ></Checkbox>
            <div className="lmn-text-color-desc">
              {/* I acknowledge Citi may collect, use, share, store, transfer and process my information
              as described in the Citi Commercial Bank Website Privacy Policy for the purposes of
              having a CCB Relationship Manager contact me. */}
              I acknowledge Citi may collect, use, share, store, transfer and process my information
              as described in the Citi Commercial Bank Website Privacy Policy.
            </div>
          </div>
          <div className="lmn-form-group">
            {errors.contactInfoConsent && <div aria-live="assertive" role="alert" id="consent_name"><small>{errors.contactInfoConsent}</small></div>}
          </div>
        </div>
      </div>
      <div>
        <FooterButtonHoc
          submitText="Continue"
          onCancel={onCancelHandler}
          onBack={onBackHandler}
          onSubmit={onSubmitHandler}
        />
      </div>
    </div>
  );
};

export default ContactInfoPage;
